/* eslint-disable @typescript-eslint/no-loop-func */
/**
 * @file: useOrderFormSections.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useCurrentUser } from '@euler/app/flows/auth/context';
import { InsuranceExpiryDateRow } from '@euler/app/flows/order/pages/OrderScreen/form/rows/InsuranceExpiryDateRow';
import {
  OrderFormRenderingContext,
  OrderFormSection,
} from '@euler/app/flows/order/types';
import { OrgUserRoleHelper } from '@euler/functions/OrgUserRoleHelper';
import { kNullVIN } from '@euler/functions/constants';
import { isExtendedPropertyTypeSupported } from '@euler/functions/order/isExtendedPropertyTypeSupported';
import { OrderFieldConfig, OrderFieldsConfig } from '@euler/model';
import { OrgUserRoleType } from '@euler/model/enum';
import { parseTagsFromString } from '@euler/utils';
import { arr2groupmap } from '@euler/utils/array';
import { ReactNode, useMemo } from 'react';
import { CarwashTypeRow } from '../form/rows/CarwashTypeRow';
import { CustomerMobileRow } from '../form/rows/CustomerMobileRow';
import { CustomerNameRow } from '../form/rows/CustomerNameRow';
import { CustomerTypeRow } from '../form/rows/CustomerTypeRow';
import { DashboardInfoRow } from '../form/rows/DashboardInfoRow';
import { EstimatedFinishTimeRow } from '../form/rows/EstimatedFinishTimeRow';
import { ExtendedPropertyRow } from '../form/rows/ExtendedPropertyRow';
import { FacadeInfoRow } from '../form/rows/FacadeInfoRow';
import { LicensePlateNoRow } from '../form/rows/LicensePlateNoRow';
import { MileageRow } from '../form/rows/MileageRow';
import { OldPartDisposalTypeRow } from '../form/rows/OldPartDisposalTypeRow';
import { OrderRemarkRow } from '../form/rows/OrderRemarkRow';
import { OrderTagsRow } from '../form/rows/OrderTagsRow';
import { ReservationTypeRow } from '../form/rows/ReservationTypeRow';
import { ReturnTypeRow } from '../form/rows/ReturnTypeRow';
import { ServiceAgentRow } from '../form/rows/ServiceAgentRow';
import { ServiceSubjectsRow } from '../form/rows/ServiceSubjectsRow';
import { VehicleInfoRow } from '../form/rows/VehicleInfoRow';
import { VehicleModelYearRow } from '../form/rows/VehicleModelYearRow';
import { VinRow } from '../form/rows/VinRow';
import {
  OrderFieldType,
  OrderPropertyName,
  kEnumOrderProperties,
  kOrderFieldTypeToPropertyNameMap,
} from './constants';

export function isOrderPropertyHidden(
  fieldsConfig: OrderFieldsConfig | undefined,
  name: OrderPropertyName,
  defaultRequired?: boolean,
) {
  const required = fieldsConfig?.[name]?.required ?? defaultRequired;
  const defaultValue = fieldsConfig?.[name]?.defaultValue;
  const hasDefaultValue =
    defaultValue != null &&
    ((name === 'mileage' &&
      typeof defaultValue === 'number' &&
      defaultValue >= 0) ||
      (name === 'tags' &&
        typeof defaultValue === 'string' &&
        parseTagsFromString(defaultValue).length > 0) ||
      (kEnumOrderProperties.has(name) &&
        typeof defaultValue === 'number' &&
        defaultValue >= 0) ||
      (name === 'vin' && defaultValue === kNullVIN) ||
      name === 'dashboardImage' ||
      name === 'facadeInspection');

  return Boolean(
    fieldsConfig?.[name]?.hidden && (!required || hasDefaultValue),
  );
}

function canUpdateField(
  ctx: OrderFormRenderingContext,
  roleHelper: OrgUserRoleHelper,
  updatableBy: OrgUserRoleType[] | undefined,
) {
  if (ctx.mode === 'new') return true;
  const updatableByRoles = updatableBy?.length
    ? updatableBy
    : ctx.storeConfiguration.restrictedRolesForUpdateOrder?.length
    ? ctx.storeConfiguration.restrictedRolesForUpdateOrder
    : [
        OrgUserRoleType.ServiceAgents,
        OrgUserRoleType.Technicians,
        OrgUserRoleType.Manager,
      ];
  return roleHelper.hasAnyRole(...updatableByRoles);
}

export function useOrderFormSections(ctx: OrderFormRenderingContext) {
  const { mode, type, storeConfiguration, isEntryOrder, hasChildOrders } = ctx;
  const user = useCurrentUser();
  const roleHelper = useMemo(
    () => new OrgUserRoleHelper(user?.user.role),
    [user?.user.role],
  );

  return useMemo<OrderFormSection[]>(() => {
    const sections: OrderFormSection[] = [];
    const sectionMap = new Map<string, OrderFormSection>();
    const fieldsConfig = storeConfiguration.app?.orderFlow?.fields;

    const extendedPropertyGroups = arr2groupmap(
      storeConfiguration.extendedOrderProperties?.filter(x =>
        isExtendedPropertyTypeSupported(x.type),
      ) ?? [],
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      x => x.group?.trim() || '其他信息',
    );

    const makeSection = (name: string, renderTitleRight?: () => ReactNode) => {
      let section = sectionMap.get(name);
      if (!section) {
        section = { name, fields: [], renderTitleRight };
        sectionMap.set(name, section);
        sections.push(section);
      }
      return section;
    };

    type FieldBuilder = (
      withField: (
        field: OrderFieldType,
        defaultRequired?: boolean,
        render?: () => ReactNode,
        shouldHide?: () => boolean,
      ) => void,
      section: OrderFormSection,
    ) => void;

    const withSection = (name: string, builder: FieldBuilder) => {
      const section = makeSection(name);
      builder((field, defaultRequired, render, shouldHide) => {
        const propertyName = kOrderFieldTypeToPropertyNameMap.get(field);
        if (
          mode === 'update' ||
          propertyName == null ||
          (shouldHide
            ? !shouldHide()
            : !isOrderPropertyHidden(
                fieldsConfig,
                propertyName,
                defaultRequired,
              ))
        ) {
          section.fields.push({
            render:
              render ??
              (() =>
                defaultFieldRender(
                  ctx,
                  roleHelper,
                  field,
                  propertyName
                    ? fieldsConfig?.[propertyName]?.required ?? defaultRequired
                    : defaultRequired,
                  propertyName ? fieldsConfig?.[propertyName] : undefined,
                )),
          });
        }
      }, section);
    };

    const withSectionAlternative = (
      defaultName: string,
      fieldBuilder: (
        withField: (
          field: OrderFieldType,
          defaultRequired?: boolean,
          render?: () => ReactNode,
          shouldHide?: () => boolean,
        ) => void,
      ) => void,
    ) => {
      fieldBuilder((field, defaultRequired, render, shouldHide) => {
        const propertyName =
          field === 'vehicleInfo'
            ? kOrderFieldTypeToPropertyNameMap.get('vin')
            : kOrderFieldTypeToPropertyNameMap.get(field);
        const sectionName =
          propertyName == null
            ? defaultName
            : // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
              fieldsConfig?.[propertyName]?.group?.trim() || defaultName;
        withSection(sectionName, withField =>
          withField(field, defaultRequired, render, shouldHide),
        );
      });
    };

    const firstSectionName = mode === 'new' ? '车辆基本信息' : '车辆信息';

    if (mode === 'new') {
      const shouldHideServiceAgent = () =>
        Boolean(
          fieldsConfig?.serviceAgent?.hidden &&
            user?.user.role.includes(OrgUserRoleType.ServiceAgents),
        );

      if (type === 'main') {
        withSectionAlternative(firstSectionName, withField => {
          withField('licensePlateNo', true);
          withField('vin', true);
          if (
            !fieldsConfig?.vin?.hidden ||
            fieldsConfig.vin.defaultValue !== kNullVIN
          ) {
            withField('vehicleInfo', true);
            withField('vehicleModelYear');
          }
        });

        // for new order form
        //#region new order creation form

        withSectionAlternative('车辆进厂状态记录', withField => {
          withField('mileage', true);
          withField('dashboardInfo', true);
          withField('facadeInfo', true);
        });

        withSectionAlternative('服务信息', withField => {
          withField('serviceAgent', true, undefined, shouldHideServiceAgent);
          withField('orderTags', true);
          withField('serviceSubjects');
          withField('remark');
        });

        withSectionAlternative('洗车及送还车等信息', withField => {
          withField('carwashType');
          withField('returnType');
          withField('oldPartDisposalType');
          withField('estimatedFinishTime');
        });

        withSectionAlternative('客户信息', withField => {
          withField('reservationType');
          withField('customerType');
          withField('customerName');
          withField('customerMobile');
          withField('insuranceExpiryDate');
        });

        //#endregion
      } else {
        withSectionAlternative('子订单服务信息', withField => {
          withField('serviceAgent', true, undefined);
          withField('orderTags', true);
          withField('estimatedFinishTime');
          withField('serviceSubjects');
          withField('remark');
        });
      }
    } else {
      // for order update form
      if (type === 'main') {
        withSection(firstSectionName, withField => {
          withField('licensePlateNo', true);
          withField('vin', true);
          withField('vehicleInfo', true);
          withField('vehicleModelYear');
          withField('mileage', true);
          if (!hasChildOrders) {
            withField('dashboardInfo');
            withField('facadeInfo');
          }
        });
      } else if (isEntryOrder) {
        withSection(firstSectionName, withField => {
          withField('dashboardInfo');
          withField('facadeInfo');
        });
      }
      withSection('服务信息', withField => {
        if (!hasChildOrders) {
          withField('orderTags', true);
        }
        withField('serviceAgent', true);
        withField('estimatedFinishTime');
        withField('remark');
        if (!hasChildOrders) {
          withField('serviceSubjects');
        }
      });
      if (type === 'main') {
        withSection('客户信息', withField => {
          withField('customerType');
          withField('customerName');
          withField('customerMobile');
          withField('insuranceExpiryDate');
          withField('carwashType');
          withField('returnType');
          withField('oldPartDisposalType');
          withField('reservationType');
        });
      }
    }

    if (type === 'main') {
      for (const [group, properties] of extendedPropertyGroups.entries()) {
        withSection(group, (_withField, section) => {
          for (const property of properties) {
            if (
              !fieldsConfig?.extendedProperties?.hidden ||
              (property.required &&
                (property.type === 'text' ||
                  property.type === 'switch' ||
                  property.type === 'assets' ||
                  (property.type === 'select' &&
                    !property.options.some(x => x.isDefaultSelected))))
            )
              section.fields.push({
                render: () => (
                  <ExtendedPropertyRow
                    key={`extended-property-${property.id}`}
                    property={property}
                    required={property.required}
                    disabled={
                      !canUpdateField(
                        ctx,
                        roleHelper,
                        property.updatableBy?.length
                          ? property.updatableBy
                          : fieldsConfig?.extendedProperties?.updatableBy,
                      )
                    }
                  />
                ),
              });
          }
        });
      }
    }

    return sections;
  }, [
    ctx,
    hasChildOrders,
    isEntryOrder,
    mode,
    roleHelper,
    storeConfiguration.app?.orderFlow?.fields,
    storeConfiguration.extendedOrderProperties,
    type,
    user?.user.role,
  ]);
}

function defaultFieldRender(
  ctx: OrderFormRenderingContext,
  roleHelper: OrgUserRoleHelper,
  field: OrderFieldType,
  required: boolean | undefined,
  fieldConfig: OrderFieldConfig | undefined,
) {
  const disabled = !canUpdateField(ctx, roleHelper, fieldConfig?.updatableBy);
  switch (field) {
    case 'licensePlateNo': {
      return (
        <LicensePlateNoRow
          key={field}
          required={required}
          disabled={disabled}
        />
      );
    }
    case 'vin': {
      return <VinRow key={field} required={required} disabled={disabled} />;
    }
    case 'vehicleInfo': {
      return (
        <VehicleInfoRow key={field} required={required} disabled={disabled} />
      );
    }
    case 'vehicleModelYear': {
      return <VehicleModelYearRow key={field} disabled={disabled} />;
    }
    case 'mileage': {
      return (
        <MileageRow
          key={field}
          required={required}
          defaultValue={fieldConfig?.defaultValue}
          disabled={disabled}
        />
      );
    }
    case 'dashboardInfo': {
      return (
        <DashboardInfoRow
          key={field}
          required={ctx.mode === 'update' ? false : required}
          disabled={disabled}
        />
      );
    }
    case 'facadeInfo': {
      return (
        <FacadeInfoRow key={field} required={required} disabled={disabled} />
      );
    }
    case 'serviceAgent': {
      return (
        <ServiceAgentRow key={field} required={required} disabled={disabled} />
      );
    }
    case 'orderTags': {
      return (
        <OrderTagsRow key={field} required={required} disabled={disabled} />
      );
    }
    case 'serviceSubjects': {
      return (
        <ServiceSubjectsRow
          key={field}
          required={ctx.mode === 'update' ? false : required}
          disabled={disabled}
        />
      );
    }
    case 'remark': {
      return (
        <OrderRemarkRow key={field} required={required} disabled={disabled} />
      );
    }
    case 'carwashType': {
      return (
        <CarwashTypeRow key={field} required={required} disabled={disabled} />
      );
    }
    case 'returnType': {
      return (
        <ReturnTypeRow key={field} required={required} disabled={disabled} />
      );
    }
    case 'oldPartDisposalType': {
      return (
        <OldPartDisposalTypeRow
          key={field}
          required={required}
          disabled={disabled}
        />
      );
    }
    case 'estimatedFinishTime': {
      return (
        <EstimatedFinishTimeRow
          key={field}
          required={required}
          disabled={disabled}
        />
      );
    }
    case 'reservationType': {
      return (
        <ReservationTypeRow
          key={field}
          required={required}
          disabled={disabled}
        />
      );
    }
    case 'customerType': {
      return (
        <CustomerTypeRow key={field} required={required} disabled={disabled} />
      );
    }
    case 'customerName': {
      return (
        <CustomerNameRow key={field} required={required} disabled={disabled} />
      );
    }
    case 'customerMobile': {
      return (
        <CustomerMobileRow
          key={field}
          required={required}
          disabled={disabled}
        />
      );
    }
    case 'insuranceExpiryDate': {
      return (
        <InsuranceExpiryDateRow
          key={field}
          required={required}
          disabled={disabled}
        />
      );
    }
    default:
      return null;
  }
}
